import React, { Component, createRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import SwalServices from '../../services/swalServices/SwalServices';
import AdminServices from '../../services/axiosServices/apiServices/adminServices';
import { ProgressSpinner } from 'primereact/progressspinner';
import { CommonSuccessMessages } from '../../utils/Messages';
import moment from 'moment';

export default class EOMSubmissions extends Component {
    constructor(props) {
        super(props);
        this.swalServices = new SwalServices();
        this.adminServices = new AdminServices();
        this.dt = createRef();
        this.state = {
            isLoading: false,
            isLoadingMonths: true,
            nominations: [],
            total: 0,
            selectedMonth: null,
            displayMonth: '',
            availableMonths: [],
        };
        this.exportCSV = this.exportCSV.bind(this);
        this.exportExcel = this.exportExcel.bind(this);
        this.saveAsExcelFile = this.saveAsExcelFile.bind(this);
    }

    componentDidMount() {
        this.loadAvailableMonths();
    }

    loadAvailableMonths = async () => {
        this.setState({ isLoadingMonths: true });
        try {
            const response = await this.adminServices.getEOMAvailableMonths();

            if (response?.success === 1 && response.data && response.data.length > 0) {
                const months = response.data;
                const defaultMonth = months[0];
                const defaultDate = moment(defaultMonth, 'YYYY-MM').toDate();

                this.setState({
                    availableMonths: months,
                    selectedMonth: defaultDate,
                    displayMonth: defaultMonth,
                    isLoadingMonths: false
                }, () => {
                    this.getNominations();
                });
            } else {
                // No available months - set current month as default
                const currentMonth = moment().format('YYYY-MM');
                this.setState({
                    availableMonths: [],
                    selectedMonth: new Date(),
                    displayMonth: currentMonth,
                    isLoadingMonths: false
                }, () => {
                    this.getNominations();
                });
            }
        } catch (error) {
            console.error('Error loading available months:', error);
            const currentMonth = moment().format('YYYY-MM');
            this.setState({
                availableMonths: [],
                selectedMonth: new Date(),
                displayMonth: currentMonth,
                isLoadingMonths: false
            }, () => {
                this.getNominations();
            });
        }
    };

    getNominations = () => {
        this.setState({ isLoading: true });
        const { displayMonth } = this.state;

        if (!displayMonth) {
            this.setState({ isLoading: false });
            return;
        }

        this.adminServices.getEOMNominations([displayMonth, 0, -1]).then((response) => {
            if (response?.success === 1) {
                this.setState({
                    nominations: response.data || [],
                    total: response.total || 0
                });
            }
            this.setState({ isLoading: false });
        }).catch(() => this.setState({ isLoading: false }));
    };

    handleMonthChange = (e) => {
        const selectedDate = e.value;
        if (selectedDate) {
            const monthStr = moment(selectedDate).format('YYYY-MM');
            this.setState({
                selectedMonth: selectedDate,
                displayMonth: monthStr
            }, () => {
                this.getNominations();
            });
        }
    };

    deleteNomination = (id) => {
        this.swalServices.Confirm("", "", CommonSuccessMessages.Yes_Text, CommonSuccessMessages.No_Text, "Delete this nomination?").then((confirmed) => {
            if (confirmed) {
                this.adminServices.deleteEOMNomination(id).then((response) => {
                    if (response?.success === 1) {
                        this.swalServices.Success(response.msg);
                        this.getNominations();
                    } else {
                        this.swalServices.Error(response?.msg || 'Failed to delete');
                    }
                });
            }
        });
    };

    formatDate = (dateStr) => {
        return moment(dateStr).format('DD/MM/YYYY HH:mm');
    };

    actionBodyTemplate = (rowData) => {
        return (
            <button
                onClick={() => this.deleteNomination(rowData.id)}
                className='py-[6px] px-[10px] text-[12px] font-medium rounded-[6px] text-red-600 bg-red-50 hover:bg-red-100 border-0'
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        );
    };

    canContactTemplate = (rowData) => {
        return rowData.can_contact === 1 ? (
            <span className='text-green-600 font-medium'>Yes</span>
        ) : (
            <span className='text-red-600 font-medium'>No</span>
        );
    };

    exportCSV(selectionOnly) {
        if (this.dt.current) {
            const data = selectionOnly ? this.dt.current.props.value.filter(row => this.dt.current.isSelected(row)) : this.dt.current.props.value;

            let csv = '';
            let columns = this.dt.current.props.children;

            columns = columns.filter(col => col.props.header !== 'Action');
            csv += columns.map(col => col.props.header).join(',') + '\n';

            data.forEach(rowData => {
                const row = columns.map(col => {
                    const field = col.props.field;
                    if (field === 'can_contact') {
                        return '"' + (rowData[field] === 1 ? 'Yes' : 'No') + '"';
                    }
                    if (field === 'createddate') {
                        return '"' + moment(rowData[field]).format('DD/MM/YYYY HH:mm') + '"';
                    }
                    return '"' + (rowData[field] !== undefined ? rowData[field] : '') + '"';
                });
                csv += row.join(',') + '\n';
            });

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const filename = `EOM Nominations - ${moment(this.state.displayMonth, 'YYYY-MM').format('MMMM YYYY')}.csv`;

            if (navigator.msSaveBlob) {
                navigator.msSaveBlob(blob, filename);
            } else {
                const url = URL.createObjectURL(blob);
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    }

    saveAsExcelFile(buffer, fileName) {
        import('file-saver').then((module) => {
            if (module && module.default) {
                let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                let EXCEL_EXTENSION = '.xlsx';
                const data = new Blob([buffer], { type: EXCEL_TYPE });

                module.default.saveAs(data, fileName + EXCEL_EXTENSION);
            }
        });
    }

    exportExcel() {
        import('xlsx').then((xlsx) => {
            const modifiedData = this.state.nominations.map((item, index) => ({
                'Sr No': index + 1,
                'Nominator Name': item.nominator_name,
                'Nominator Email': item.nominator_email,
                'Role/Relation': item.nominator_role,
                'Nominee Name': item.nominee_name,
                'Nominee Role': item.nominee_job_role || '',
                'Location': item.nominee_location,
                'Reason': item.reason,
                'Can Contact': item.can_contact === 1 ? 'Yes' : 'No',
                'Submitted': moment(item.createddate).format('DD/MM/YYYY HH:mm')
            }));
            const worksheet = xlsx.utils.json_to_sheet(modifiedData);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
            const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });

            this.saveAsExcelFile(excelBuffer, `EOM Nominations - ${moment(this.state.displayMonth, 'YYYY-MM').format('MMMM YYYY')}`);
        });
    }

    render() {
        return (
            <div className='eom-submissions-page' style={{ height: "calc((100dvh / 0.85) - 28.8px)" }}>
                <div className='eom-submissions-inner h-full p-[30px] overflow-y-auto'>
                    <div className='title flex items-center justify-between mb-[10px]'>
                        <h1 className='text-[22px] m-0 font-semibold text-[#004AAD]' style={{ lineHeight: "30px" }}>
                            EOM Submissions
                        </h1>
                    </div>

                    {/* Nominations Table */}
                    <div className='nominations-section p-5 bg-white rounded-[16px]' style={{ border: "1px solid #DFDFDF" }}>
                        <div className='flex items-center justify-between mb-4'>
                            <h2 className='text-[16px] font-semibold m-0'>
                                Nominations ({this.state.total})
                            </h2>
                            <div className='flex items-center gap-2'>
                                <label className='text-sm font-medium text-gray-600'>Month:</label>
                                {this.state.isLoadingMonths ? (
                                    <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="4" />
                                ) : (
                                    <Calendar
                                        value={this.state.selectedMonth}
                                        onChange={this.handleMonthChange}
                                        view="month"
                                        dateFormat="MM yy"
                                        placeholder="Select Month"
                                        maxDate={new Date()}
                                        yearNavigator
                                        yearRange={`2020:${moment().year()}`}
                                        className="month-picker"
                                        style={{ width: '180px' }}
                                        showIcon
                                    />
                                )}
                            </div>
                        </div>

                        {this.state.isLoadingMonths || this.state.isLoading ? (
                            <div className='flex justify-center items-center py-10'>
                                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                            </div>
                        ) : this.state.nominations.length === 0 ? (
                            <div className='text-center py-10 text-gray-500'>
                                <p>No nominations found for {moment(this.state.displayMonth, 'YYYY-MM').format('MMMM YYYY')}</p>
                            </div>
                        ) : (
                            <div className='rounded-xl shadow-sm overflow-hidden border border-gray-200 bg-white'>
                                <DataTable
                                    ref={this.dt}
                                    value={this.state.nominations}
                                    paginator
                                    rows={20}
                                    responsiveLayout="scroll"
                                    scrollable
                                    scrollHeight="calc((100dvh / 0.85) - 350px)"
                                    className="min-w-full"
                                    emptyMessage="No nominations found"
                                    paginatorRight={
                                        <div className="flex gap-2">
                                            <Button label="Export CSV" icon="pi pi-file" className="export-btn" onClick={() => this.exportCSV(false)} />
                                            <Button label="Export Excel" icon="pi pi-file-excel" className="export-btn" onClick={this.exportExcel} />
                                        </div>
                                    }
                                >
                                    <Column
                                        header="#"
                                        body={(rowData, options) => options.rowIndex + 1}
                                        style={{ width: '50px' }}
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                    />
                                    <Column
                                        field="nominator_name"
                                        header="Nominator"
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                        sortable
                                    />
                                    <Column
                                        field="nominator_email"
                                        header="Email"
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                    />
                                    <Column
                                        field="nominator_role"
                                        header="Role/Relation"
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                    />
                                    <Column
                                        field="nominee_name"
                                        header="Nominee"
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                        sortable
                                    />
                                    <Column
                                        field="nominee_job_role"
                                        header="Nominee Role"
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                    />
                                    <Column
                                        field="nominee_location"
                                        header="Location"
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                    />
                                    <Column
                                        field="reason"
                                        header="Reason"
                                        style={{ maxWidth: '300px' }}
                                        body={(rowData) => (
                                            <div className='truncate' title={rowData.reason} style={{ maxWidth: '280px' }}>
                                                {rowData.reason}
                                            </div>
                                        )}
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                    />
                                    <Column
                                        field="can_contact"
                                        header="Can Contact"
                                        body={this.canContactTemplate}
                                        style={{ width: '100px' }}
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                    />
                                    <Column
                                        field="createddate"
                                        header="Submitted"
                                        body={(rowData) => this.formatDate(rowData.createddate)}
                                        style={{ width: '150px' }}
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                        sortable
                                    />
                                    <Column
                                        header="Action"
                                        body={this.actionBodyTemplate}
                                        style={{ width: '80px', textAlign: 'center' }}
                                        headerClassName="bg-blue-50 text-blue-800 font-medium"
                                    />
                                </DataTable>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
